import type { Tulip, TulipShape } from './types'

/**
 * Die Tulpen-Notation: welche Formen es gibt und wie sie gerechnet werden.
 *
 * Hier steht alles, was sich ohne Bildschirm entscheiden lässt - der Katalog
 * der Formen, das Einrasten der Richtung, die Punkte des Zeichens. Gezeichnet
 * wird daraus in components/Tulip.tsx; dass die Rechnung hier und ohne DOM
 * liegt, ist der Grund, warum sie geprüft werden kann.
 *
 * Warum ein eigener Katalog und keine fremde Sammlung: die verbreiteten
 * Glyphensätze sind Dakar-Notation und enthalten Dünen, Brunnen und
 * Hubschrauberplätze. Für eine Fahrt Schweiz - Balkan - Schwarzes Meer sind
 * das mehrere hundert Zeichen, von denen dreizehn gebraucht werden. Also
 * dreizehn, jedes mit einer Stelle der Reise als Begründung.
 *
 * Fünfzehn, seit zwei Formen dazugekommen sind, die keine Stelle dieser Reise
 * als Begründung haben und trotzdem hineingehören: die Fähre und der Schotter.
 * Beide sind der Gegenprobe zur ersten Regel entsprungen - ein Katalog, der nur
 * die gefahrene Strecke kennt, ist beim zweiten Mal falsch. Die Fähre fehlte,
 * weil auf dem Balkan überall eine Brücke stand; sobald es über ein Meer
 * zurückgeht, ist sie das wichtigste Zeichen von allen. Der Schotter stand
 * schon da, nur als Notiz statt als Zeichen ("rechts, Schotter" bei km 118) -
 * und in einem T3 ist der Belag eine Fahranweisung und keine Beobachtung.
 *
 * Der Massstab für eine sechzehnte Form ist damit derselbe geblieben, nur
 * schärfer: nicht "kommt auf dieser Reise vor", sondern "kommt auf jeder vor,
 * und ohne Zeichen steht die Auskunft in keiner Zeile".
 *
 * Das Koordinatensystem der Zeichen ist ein Quadrat von 100 auf 100 mit dem
 * Mittelpunkt (50, 50). Es wird im Buch auf rund 19 mm gesetzt, eine Einheit
 * ist also gut ein Fünftel Millimeter. Alle Masse unten sind mit dieser
 * Umrechnung gewählt: eine Linie von 2 Einheiten ist knapp 0.4 mm breit und
 * überlebt eine Schwarzweisskopie, eine von 0.5 nicht.
 */

/** Mittelpunkt des Zeichens. */
export const MITTE = 50

/** Radius des Knotenkreises. */
export const KNOTEN_R = 13

/** Radius des Rings beim Kreisverkehr, plus der Insel darin. */
export const RING_R = 19
export const INSEL_R = 7.5

/** Wo ein Arm aufhört. Wo er anfängt, sagt der Radius des Kreises. */
export const ARM_AUSSEN = 42

/** Der Punkt, aus dem man kommt: unten, am Ende des Zufahrtsstrichs. */
export const HERKUNFT_R = 42
export const PUNKT_R = 5

/** Strichbreiten: der zu fahrende Weg gegen alles andere. */
export const STRICH_FAHRT = 3.6
export const STRICH_WEG = 2.1

/** Der Pfeil: Spitze, Grundlinie, halbe Breite. */
export const PFEIL_SPITZE = 48
export const PFEIL_BASIS = 36
export const PFEIL_BREITE = 7

/** Wonach eine Form aussieht: ein Knotenpunkt oder eine Stelle der Strecke. */
export type TulipGroup = 'knoten' | 'strecke'

export interface TulipShapeInfo {
  key: TulipShape
  /** Name in der Auswahlliste und im Vorlesen. */
  label: string
  group: TulipGroup
  /** Wofür die Form auf dieser Reise da ist. Steht in der Auswahl daneben. */
  hint: string
  /**
   * Was das Zeichen sagt, für jemanden, der noch nie eines gesehen hat.
   *
   * Steht so auf der Legendenseite im Buch (features/print/BookBack.tsx) und
   * ist deshalb etwas anderes als hint: der Hinweis sagt der planenden Person,
   * wofür sie die Form nimmt, dieser Satz sagt der lesenden Person, was auf dem
   * Papier zu sehen ist und was sie damit anfangen soll. Beschrieben wird
   * zuerst die Zeichnung und dann ihre Bedeutung - wer den Satz liest, hat das
   * Zeichen daneben vor sich und soll es wiedererkennen, nicht auswendig
   * lernen.
   *
   * Die Länge ist gedeckelt, siehe LEGENDE_ZEICHEN_MAX in
   * features/print/sections.ts: die Legendenseite hat je Eintrag vier Zeilen,
   * und was darüber hinausgeht, schneidet overflow:hidden weg -
   * stillschweigend. Der Deckel steht dort und nicht hier, weil er aus der
   * Geometrie der Seite folgt und nicht aus der Notation.
   */
  legende: string
  /**
   * Richtungen, die diese Form kennt, in Grad.
   *
   * Bei den Knoten sind das die Arme, die gezeichnet werden. Bei Auffahrt und
   * Abfahrt ist es die Seite, auf der die Rampe liegt. Eine leere Liste heisst:
   * die Form kennt keine Wahl, die Strasse laeuft geradeaus durch.
   */
  directions: number[]
}

/**
 * Die Formen dieser Reise.
 *
 * Knotenpunkte zuerst, dann die Stellen der Strecke - das ist auch die
 * Reihenfolge der Auswahlliste. Die Begründung steht bei jeder Form: sie ist
 * der Grund, warum sie da ist, und der Massstab, an dem eine weitere gemessen
 * wird.
 */
export const TULIP_SHAPES: TulipShapeInfo[] = [
  {
    key: 'kreisel',
    label: 'Kreisverkehr',
    group: 'knoten',
    // Slowenien, Kroatien, Ungarn und Rumänien sind voll davon, und genau hier
    // ist die Skizze dem Satz überlegen: "zweite rechts" liest sich langsamer,
    // als der Pfeil zu sehen ist.
    hint: 'Ring mit Insel, Ausfahrt links, geradeaus oder rechts',
    legende:
      'Ein Ring mit einer Insel in der Mitte ist ein Kreisverkehr. Der Pfeil zeigt, an welcher Ausfahrt man ihn wieder verlässt.',
    directions: [270, 0, 90],
  },
  {
    key: 'kreuzung',
    label: 'Kreuzung',
    group: 'knoten',
    hint: 'vier Arme, es geht in jede Richtung weiter',
    legende:
      'Vier Strassen treffen sich. Der Pfeil zeigt, welche der drei Möglichkeiten zu nehmen ist, gezählt von unten, wo man herkommt.',
    directions: [270, 0, 90],
  },
  {
    key: 't',
    label: 'T-Kreuzung',
    group: 'knoten',
    // Die häufigste Stelle, an der man falsch fährt: geradeaus geht nicht,
    // und ohne Notiz entscheidet man es im Vorbeifahren.
    hint: 'die Strasse endet, nur links oder rechts',
    legende:
      'Die Strasse endet an einer Querstrasse. Geradeaus geht es nicht weiter, nur nach links oder nach rechts; der Pfeil sagt, wohin.',
    directions: [270, 90],
  },
  {
    key: 'gabel',
    label: 'Gabelung',
    group: 'knoten',
    hint: 'zwei Wege, keiner davon geradeaus',
    legende:
      'Die Strasse teilt sich, und keiner der beiden Äste geht geradeaus. Der dicke Ast mit dem Pfeil ist der richtige.',
    directions: [315, 45],
  },
  {
    key: 'abzweig',
    label: 'Abzweig',
    group: 'knoten',
    // Die M25 durch die Djerdap-Schlucht: die Hauptstrasse laeuft der Donau
    // nach, und alle paar Kilometer geht ein Weg in ein Dorf ab.
    hint: 'die Hauptstrasse läuft weiter, ein Weg geht ab',
    legende:
      'Die Strasse, auf der man fährt, läuft weiter, und daneben geht ein Weg ab. Dick gezeichnet ist der, den man nimmt.',
    directions: [270, 315, 45, 90],
  },
  {
    key: 'auffahrt',
    label: 'Autobahnauffahrt',
    group: 'strecke',
    hint: 'Rampe auf die Autobahn, rechts oder links',
    legende:
      'Die beiden langen Striche sind die Autobahn. Die Rampe führt von unten auf sie hinauf und wird selbst zur gefahrenen Spur.',
    directions: [90, 270],
  },
  {
    key: 'abfahrt',
    label: 'Autobahnabfahrt',
    group: 'strecke',
    hint: 'Rampe von der Autobahn herunter',
    legende:
      'Von der Autobahn herunter: die gefahrene Spur beginnt zwischen den beiden langen Strichen und führt seitlich von ihnen weg.',
    directions: [90, 270],
  },
  {
    key: 'grenze',
    label: 'Grenzübergang',
    group: 'strecke',
    // Acht Grenzen auf sechzehn Tagen. Was dort zu zeigen ist - Pass, Grüne
    // Karte, Bargeld - steht im Hinweis daneben.
    hint: 'Landesgrenze, Papiere bereithalten',
    legende:
      'Die Strich-Punkt-Linie quer über die Strasse ist eine Landesgrenze, wie auf jeder Landkarte. Papiere und Bargeld bereithalten.',
    directions: [],
  },
  {
    key: 'maut',
    label: 'Mautstelle',
    group: 'strecke',
    // Kroatien kassiert an Stationen, dazu der Karawankentunnel und die
    // Donaubrücke Ruse-Giurgiu. Anhalten, Karte oder Bargeld.
    hint: 'Schranke mit Häuschen, anhalten und zahlen',
    legende:
      'Ein Dach über der Fahrbahn, links und rechts ein Häuschen: eine Mautstelle. Hier wird angehalten und gezahlt.',
    directions: [],
  },
  {
    key: 'faehre',
    label: 'Fähre',
    group: 'strecke',
    // Auf der ersten Reise gab es keine - nach Pag geht die Brücke, über die
    // Donau die Brücke. Weltweit ist sie eines der wichtigsten Zeichen
    // überhaupt: Ostsee, Adria, die dalmatinischen Inseln, Schottland,
    // Norwegen. Und sie ist das einzige Zeichen des Katalogs, das keine Stelle
    // meint, sondern einen Termin - wer sie verpasst, verliert Stunden.
    // Deshalb steht im Hinweis die Abfahrtszeit, siehe
    // TULPEN_NOTIZ_ZEICHEN_MAX.
    hint: 'Schiff statt Strasse - Abfahrtszeit dazuschreiben',
    legende:
      'Ein Schiff auf dem Wasser: hier setzt eine Fähre über. Wann sie fährt, steht im Hinweis - eine verpasste kostet Stunden.',
    directions: [],
  },
  {
    key: 'tunnel',
    label: 'Tunnel',
    group: 'strecke',
    // Karawankentunnel, die Röhren der M25 an der Donau, der Bâlea-Tunnel oben
    // auf dem Transfăgărășan.
    hint: 'Röhre, Licht an',
    legende:
      'Das Tor ist der Tunneleingang. Drinnen ist die Strasse gestrichelt: sie ist da, man sieht sie nur nicht. Licht einschalten.',
    directions: [],
  },
  {
    key: 'serpentine',
    label: 'Kehren',
    group: 'strecke',
    // Vršič mit fünfzig Kehren, dazu Transfăgărășan und Transalpina. Der
    // eigentliche Anwendungsfall: hier steht im Hinweis der Gang.
    hint: 'Serpentinen, Steigung oder Gefälle',
    legende:
      'Der Zickzack steht für eine Folge von Kehren, bergauf oder bergab. Wie viele es sind, steht als Hinweis daneben.',
    directions: [],
  },
  {
    key: 'schotter',
    label: 'Schotter',
    group: 'strecke',
    // Stand im eigenen Bestand schon da, nur als Notiz: "rechts, Schotter" bei
    // km 118. In einem T3 ist eine unbefestigte Strasse eine Fahranweisung -
    // Tempo herunter, Abstand halten, und die Frage, ob der Anhänger da
    // durchkommt. Weltweit häufiger als eine Mautstelle.
    //
    // Er wird vorgeschlagen, seit der Belag aus OpenStreetMap kommt: der
    // Routendienst nennt die Knoten der befahrenen Wege, und damit lässt sich
    // der Weg nachschlagen (backend/internal/surface, lib/surface.ts). Was der
    // Vorschlag daraus macht, steht in lib/tulipSuggest.ts. Angestossen wird
    // die Suche von Hand, weil sie einen langsamen Dienst kostet - eine Etappe
    // ohne diesen Schritt hat also keine Schotterzeile, und das heisst nicht,
    // dass sie keinen Schotter hat.
    hint: 'unbefestigt, Schotter oder Piste',
    legende:
      'Die Strasse hat keinen Belag: die aufgelösten Ränder heissen Schotter oder Piste. Tempo herunter, auf lose Steine gefasst.',
    directions: [],
  },
  {
    key: 'bahn',
    label: 'Bahnübergang',
    group: 'strecke',
    // Unbeschrankt und schlecht sichtbar auf den Landstrassen in Serbien,
    // Bulgarien und Rumänien.
    hint: 'Gleise queren die Strasse',
    legende:
      'Zwei Schienen mit Schwellen queren die Strasse: ein Bahnübergang. Oft ohne Schranke, also selbst schauen und hören.',
    directions: [],
  },
  {
    key: 'ort',
    label: 'Ortsdurchfahrt',
    group: 'strecke',
    hint: 'Häuser an der Strasse, Tempo herunter',
    legende:
      'Häuser links und rechts der Strasse: es geht mitten durch einen Ort. Tempo herunter, oft schon vor dem Ortsschild.',
    directions: [],
  },
]

const NACH_SCHLUESSEL = new Map(TULIP_SHAPES.map((info) => [info.key, info]))

/**
 * Die Form, auf die Unbekanntes zurückfällt.
 *
 * Die Kreuzung, weil sie die harmloseste Falschaussage ist: sie zeigt, dass
 * hier etwas ist, und behauptet keine Richtung, die es nicht gibt.
 */
const RUECKFALL: TulipShapeInfo = NACH_SCHLUESSEL.get('kreuzung') ?? TULIP_SHAPES[0]!

/** Die Beschreibung einer Form. Unbekanntes fällt auf die Kreuzung zurück. */
export function tulipShapeInfo(shape: TulipShape): TulipShapeInfo {
  return NACH_SCHLUESSEL.get(shape) ?? RUECKFALL
}

export function isTulipShape(value: unknown): value is TulipShape {
  return typeof value === 'string' && NACH_SCHLUESSEL.has(value as TulipShape)
}

/**
 * Wie lang der Hinweis einer Tulpe höchstens sein darf.
 *
 * Siebzig Zeichen. Die Rechnung: neben dem 19 mm breiten Zeichen und dem 9.2 mm
 * breiten Eintragefeld (.dr-tulpen-hand in print.css) bleiben in der halben
 * Spalte 83.5 px, und die Reihe hat Platz für fünf Zeilen
 * (TULPEN_NOTIZ_ZEILEN_MAX in features/print/stagePlan.ts). Siebzig Zeichen
 * sind die Länge, bei der eine gewöhnliche Notiz diese fünf Zeilen genau
 * ausfüllt - gemessen im gerenderten Buch, Textsorte für Textsorte:
 *
 *     schmale Zeichen ("Ii ll ti ri")   126
 *     gewöhnliche Notiz                  70
 *     erzeugter Vorschlagstext           78
 *     durchweg breite Zeichen ("WM")     60
 *
 * Vorher standen hier hundertzehn, bei 122.1 px Spaltenbreite. Die Differenz
 * ist der Preis des Eintragefelds, und er ist bewusst bezahlt: was die
 * Anwendung selbst vorschlägt, ist auf vierzig Zeichen gedeckelt (NOTIZ_MAX in
 * lib/tulipSuggest.ts) und passt also mit Rand, und eine von Hand geschriebene
 * Zeile von siebzig Zeichen ist immer noch ein ganzer Satz. Was ein gedrucktes
 * Roadbook dagegen nicht kann, ist sich selbst berichtigen - dafür braucht es
 * den Kasten.
 *
 * Die Fähre ist der Fall, an dem sich zeigt, ob siebzig reichen. Sie ist das
 * einzige Zeichen des Katalogs, das einen Termin meint, und die Abfahrtszeit
 * steht nirgends sonst - nicht in der Form, nicht im Kilometerstand, in keiner
 * Antwort des Routendienstes. Sie muss in diese Zeile passen, und zwar
 * zusätzlich zu dem, was die Anwendung selbst vorschlägt. Nachgerechnet an der
 * längsten Fähre der Reise:
 *
 *     Vorschlag  "Prizna - Žigljen (Pag), 20 min"          30
 *     von Hand   " · ab 14:30, 30 min vorher da sein"      34
 *     zusammen                                             64
 *
 * Sechs Zeichen Luft bei einem Namen, der schon zu den längeren gehört. Der
 * Deckel des Vorschlags (NOTIZ_MAX in lib/tulipSuggest.ts, vierzig Zeichen)
 * lässt also in jedem Fall dreissig für die Zeit - genug für "ab 14:30" und
 * einen halben Satz dazu.
 *
 * Der Deckel steht hier und nicht bei der Geometrie, weil er die Eingabe
 * begrenzt und nicht den Satz: er ist die Zusage an die schreibende Person,
 * dass das, was sie eintippt, im Buch auch ganz dasteht. Die Zusage gilt nicht
 * für jede Textsorte - in Versalien geschrieben passen nur rund sechzig Zeichen
 * in fünf Zeilen. Was dann doch nicht hineinpasst, kürzt der line-clamp in
 * print.css sichtbar mit Auslassungspunkten; verschwinden kann nichts.
 *
 * Ohne diesen Deckel wuchs die Tulpenreihe mit der Notiz, und weil eine
 * Roadbookseite acht Reihen fasst und darüber hinaus abschneidet, fielen die
 * untersten Reihen einer vollen Seite ersatzlos weg. Derselbe Fehler wie bei
 * den Planungsspalten und beim Erklärsatz der Legende (LEGENDE_ZEICHEN_MAX in
 * features/print/sections.ts), nur an der Stelle, an der er am teuersten ist:
 * eine fehlende Fahranweisung merkt man an der Kreuzung.
 */
export const TULPEN_NOTIZ_ZEICHEN_MAX = 70

/**
 * Eine frische Tulpe, wie sie "Tulpe hinzufügen" anlegt.
 *
 * Ohne Kilometerstand, denn den kennt in dem Moment niemand: wer am
 * Strassenrand eine Zeile nachträgt, hat den Tacho nicht im Kopf. Eine 0 wäre
 * eine Angabe, die niemand gemacht hat.
 */
export function emptyTulip(): Tulip {
  return { km: NaN, shape: 'kreuzung', turn: 0, note: '' }
}

const RICHTUNGSNAMEN: Record<number, string> = {
  0: 'geradeaus',
  45: 'halb rechts',
  90: 'rechts',
  135: 'scharf rechts',
  180: 'zurück',
  225: 'scharf links',
  270: 'links',
  315: 'halb links',
}

/** Name einer Richtung, wie er vorgelesen wird. */
export function directionLabel(deg: number): string {
  return RICHTUNGSNAMEN[normalizeTurn(deg)] ?? 'geradeaus'
}

/**
 * Bringt eine Richtung auf das Achtelraster und in den Bereich 0 bis 315.
 *
 * Achtel und nicht feiner: bei 19 mm sind zwei Arme, die 20 Grad auseinander
 * liegen, im Auto nicht mehr zu unterscheiden - und eine Notation, die feiner
 * tut, als sie lesbar ist, lügt.
 */
export function normalizeTurn(deg: number): number {
  if (!Number.isFinite(deg)) return 0
  const raster = Math.round(deg / 45) * 45
  return ((raster % 360) + 360) % 360
}

/**
 * Die Richtung, in die der Pfeil zeigt.
 *
 * Eingerastet auf eine Richtung, die die Form tatsächlich hat: ein Pfeil, der
 * in keinen Arm zeigt, wäre eine Anweisung auf eine Strasse, die es an dieser
 * Kreuzung nicht gibt. Bei gleichem Abstand gewinnt die Richtung im
 * Uhrzeigersinn - also rechts. Das ist kein Münzwurf, sondern die Seite, auf
 * der man in Europa abbiegt, ohne den Gegenverkehr zu kreuzen.
 *
 * @returns null, wenn die Form gar keine Wahl kennt (Tunnel, Grenze, Kehren).
 */
export function exitDirection(shape: TulipShape, turn: number): number | null {
  const moeglich = tulipShapeInfo(shape).directions
  const erste = moeglich[0]
  if (erste === undefined) return null

  const ziel = normalizeTurn(turn)
  let beste = erste
  let bestesMass = abstandsMass(erste, ziel)

  for (const richtung of moeglich) {
    const mass = abstandsMass(richtung, ziel)
    if (mass < bestesMass) {
      beste = richtung
      bestesMass = mass
    }
  }
  return beste
}

/**
 * Vergleichsmass für das Einrasten: erst der Abstand, dann die Seite.
 *
 * Der Abstand wiegt in Zehnerschritten, der Gegenuhrzeigersinn bekommt eins
 * darauf. So gewinnt bei 90 gegen 90 die rechte Richtung, und zwar unabhängig
 * davon, in welcher Reihenfolge die Richtungen im Katalog stehen.
 */
function abstandsMass(richtung: number, ziel: number): number {
  const versatz = ((richtung - ziel + 540) % 360) - 180
  return Math.abs(versatz) * 10 + (versatz < 0 ? 1 : 0)
}

/**
 * Die Arme, die ein Knotenzeichen bekommt.
 *
 * Für die meisten Formen ist das die feste Liste aus dem Katalog: eine
 * T-Kreuzung hat zwei Arme, eine Kreuzung drei. Der Abzweig ist die Ausnahme -
 * dort hängt der zweite Arm daran, wohin es geht, denn ein Weg, der links
 * abgeht, ist eine andere Stelle als einer, der rechts abgeht. Streckenzeichen
 * haben keine Arme; ihre Strasse laeuft durch.
 */
export function tulipArms(shape: TulipShape, turn: number): number[] {
  const info = tulipShapeInfo(shape)
  if (info.group !== 'knoten') return []

  if (shape === 'abzweig') {
    // Der Abzweig kennt kein Geradeaus als Wahl - wer geradeaus fährt, hat
    // hier nichts zu notieren. Der abgehende Weg ist also immer ein zweiter
    // Arm, nie derselbe.
    const ab = exitDirection(shape, turn)
    return ab === null ? [0] : [0, ab].sort((a, b) => a - b)
  }
  return [...info.directions]
}

/**
 * Ein Punkt im Zeichen, aus Richtung und Abstand vom Mittelpunkt.
 *
 * 0 Grad ist oben, gezählt wird im Uhrzeigersinn - so, wie eine Richtung
 * angesagt wird. In SVG wächst y nach unten, deshalb das Minus.
 */
export function polar(angle: number, radius: number): [number, number] {
  const bogen = (angle * Math.PI) / 180
  return [MITTE + radius * Math.sin(bogen), MITTE - radius * Math.cos(bogen)]
}

/**
 * Die drei Ecken des Pfeils, als points-Attribut eines polygon.
 *
 * Eine gefüllte Fläche und keine zwei angesetzten Striche: bei 19 mm ist die
 * Spitze knapp zwei Millimeter lang, und zwei Striche in diesem Mass verlieren
 * beim Kopieren ihren Schnittpunkt.
 */
export function arrowPolygon(
  angle: number,
  tipRadius = PFEIL_SPITZE,
  baseRadius = PFEIL_BASIS,
  halfWidth = PFEIL_BREITE,
): string {
  const [sx, sy] = polar(angle, tipRadius)
  const [bx, by] = polar(angle, baseRadius)

  // Die Senkrechte zur Richtung: aus (sin, -cos) wird (cos, sin).
  const bogen = (angle * Math.PI) / 180
  const qx = Math.cos(bogen) * halfWidth
  const qy = Math.sin(bogen) * halfWidth

  return [
    `${runde(sx)},${runde(sy)}`,
    `${runde(bx + qx)},${runde(by + qy)}`,
    `${runde(bx - qx)},${runde(by - qy)}`,
  ].join(' ')
}

function runde(v: number): string {
  return String(Math.round(v * 100) / 100)
}

/**
 * Was die Tulpe sagt, in Worten.
 *
 * Nicht Beiwerk: es ist die Beschriftung des Bilds für den Vorleser des
 * Bildschirms - und die Fassung, die die Beifahrerin vorliest, wenn die Sonne
 * so auf dem Display steht, dass von der Skizze nichts zu sehen ist.
 */
export function tulipText(tulip: Tulip): string {
  const info = tulipShapeInfo(tulip.shape)
  const richtung = exitDirection(tulip.shape, tulip.turn)
  if (richtung === null) return info.label

  const wohin = directionLabel(richtung)
  if (tulip.shape === 'auffahrt' || tulip.shape === 'abfahrt') return `${info.label} ${wohin}`
  return `${info.label}, ${wohin}`
}

/**
 * Wo im Buch eine Form zum ersten Mal vorkommt.
 *
 * Die Antwort auf das Grundproblem der Notation: sie ist ausserhalb des
 * Rallyesports unbekannt, und ein Zeichen, das man erst nachschlagen muss, ist
 * im fahrenden Auto wertlos. Hinten steht die Legende - aber wer unterwegs
 * blättert, hat die Hände nicht frei. Also steht der Name des Zeichens beim
 * ersten Mal klein daneben, und nur dann: ab dem zweiten Mal ist er
 * Wiederholung, und fünfzehn Namen auf jeder Roadbookseite wären eine Spalte
 * Text neben einer Spalte Bild.
 *
 * Gezählt wird in Lesereihenfolge über das ganze Buch: die Etappen in
 * Reisereihenfolge, darin die Tulpen in ihrer Reihenfolge. Das ist genau die
 * Folge, in der die Seiten gedruckt werden - die Roadbookseiten einer Etappe
 * stehen unmittelbar hinter ihrer Etappenseite, siehe planStage.
 *
 * Eine reine Rechnung über Listen und nicht über Etappen: was eine Etappe sonst
 * noch ist, spielt für die Frage keine Rolle, und so lässt sie sich prüfen.
 *
 * @param listen Je Etappe die Tulpen dieser Etappe, in Reisereihenfolge.
 * @returns Dieselbe Schachtelung, je Tulpe wahr, wenn ihre Form hier zum ersten
 *   Mal auftaucht.
 */
export function erstesVorkommen(listen: readonly (readonly Tulip[] | undefined)[]): boolean[][] {
  const gesehen = new Set<TulipShape>()

  return listen.map((liste) =>
    (liste ?? []).map((tulpe) => {
      // Über den Katalog und nicht über den rohen Schlüssel: eine unbekannte
      // Form wird als Kreuzung gezeichnet und heisst dann auch so. Zweimal
      // "Kreuzung" nebeneinander wäre keine Auskunft, sondern ein Fehler, der
      // wie einer aussieht.
      const form = tulipShapeInfo(tulpe.shape).key
      if (gesehen.has(form)) return false
      gesehen.add(form)
      return true
    }),
  )
}

/**
 * Kilometerstand einer Tulpe, wie er links in der Zeile steht.
 *
 * Eine Nachkommastelle, und die nur, wenn sie etwas sagt: Roadbookzeilen werden
 * auf hundert Meter genau notiert, aber "12.0" liest sich im Auto langsamer als
 * "12". Was kein Kilometerstand ist, wird zum Strich - eine 0, die in
 * Wirklichkeit ein leeres Feld war, wäre eine Angabe, die es nicht gibt.
 */
export function tulipKm(km: number): string {
  if (!Number.isFinite(km) || km < 0) return '—'

  const gerundet = Math.round(km * 10) / 10
  return Number.isInteger(gerundet) ? String(gerundet) : gerundet.toFixed(1)
}

/**
 * Je Tulpe einer Etappe: wie weit es von ihr bis zur nächsten Anweisung ist.
 *
 * Die zweite Zahl der Zeile, und sie ist reine Arithmetik - die Differenz
 * zweier Kilometerstände, die beide schon dastehen. Trotzdem ist sie die
 * Auskunft, auf die es im fahrenden Fahrzeug ankommt: der Kilometerstand sagt,
 * ab wann eine Zeile gilt, und erst die Differenz sagt, wie lange bis dahin
 * Ruhe ist. Wer bei Tempo 80 auf ein Blatt sieht, soll nicht zwei Zahlen im
 * Kopf voneinander abziehen müssen.
 *
 * Gerechnet wird über die ganze Etappe und nicht über die Seite: die letzte
 * Zeile einer Roadbookseite meint die erste der nächsten, und eine Rechnung je
 * Seite behauptete an jedem Seitenwechsel ein Ende, das es nicht gibt.
 *
 * Die letzte Zeile einer Etappe bekommt die Entfernung bis zum Etappenziel und
 * nicht nichts. Die Frage des Fahrers ist an dieser Zeile dieselbe wie an jeder
 * anderen - "wie lange ist jetzt Ruhe?" -, und die Antwort ist von derselben
 * Art: Kilometer von hier bis zum Nächsten, das ihn angeht. Ein eigenes
 * Zeichen dafür wäre eine Unterscheidung, die bei Tempo 80 verlangt und nichts
 * einbringt; ob das Nächste eine Kreuzung ist oder der Campingplatz, ändert an
 * der Benutzung der Zahl nichts. Leer bliebe ausgerechnet die Zeile ohne
 * Nachfolger - die eine, nach der kein Blick mehr Hilfe findet.
 *
 * Leer bleibt die Angabe dort, wo sie nichts wäre als eine erfundene Zahl:
 *
 *  - Ohne Kilometerstand an einem der beiden Enden. Eine unterwegs notierte
 *    Tulpe hat keinen (siehe Tulip.km), und "von unbekannt bis 42" ist keine
 *    Entfernung.
 *  - Bei einer Differenz unter null. Die Reihenfolge der Tulpenliste ist die
 *    der Fahrt und wird von Hand gesetzt, die Kilometerstände auch - eine
 *    berichtigte Zahl kann die Folge kurzzeitig umkehren. Eine negative
 *    Entfernung im Buch wäre schlimmer als eine fehlende.
 *
 * @param etappeKm Die Länge der Etappe, auf derselben Skala wie die
 *   Kilometerstände der Tulpen - also die berechnete Strecke, wo es sie gibt.
 * @returns Je Tulpe die fertige Zeichenkette, etwa "+3.4", oder leer.
 */
export function tulipAbstaende(tulpen: readonly Tulip[], etappeKm: number): string[] {
  return tulpen.map((tulpe, i) => {
    const von = tulpe.km
    // Die nächste Zeile - und bei der letzten das Etappenziel.
    const bis = tulpen[i + 1]?.km ?? etappeKm
    if (!Number.isFinite(von) || von < 0 || !Number.isFinite(bis) || bis < von) return ''

    // Gerundet wird wie der Kilometerstand darüber. Zwei Rundungen in einer
    // Spalte sähen aus wie ein Rechenfehler.
    return `+${tulipKm(bis - von)}`
  })
}
